import React, { useState, useEffect } from 'react'
import {
  TextField,
  InputAdornment,
  List,
  Box,
  Button,
  Typography,
  CardContent,
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import { Search, Chat, Add, ExpandMore, Edit } from '@mui/icons-material'
import { authedAxios } from '../../../utils'
import { useParams } from 'react-router-dom'
import { useStore } from '../../../store'
import Note from './Note'
import { updateObjectWithMatchingId } from '../../../utils/array'
import useAlert from '../../../hooks/useAlert'
import DeleteConfirmModal from '../../components/DeleteConfirmModal'
import CustomerHeader from '../../components/CustomerHeader'
import SummaryCard from '../../components/SummaryCard'
import { TASK_TYPES } from '../../../constants'
import { AllTaskModals } from '../../../dashboard/Tasks'
import { grey } from '@mui/material/colors'
import Tooltip from '../../../common/Tooltip'
import useTaskStatusTypes from '../../../hooks/api/useTaskStatusTypes'

const Notes = ({
  types,
  customerQuery,
  customerStatusTypesData,
  customerContactsQuery,
  customerTicketsQuery,
  dropdowns,
}) => {
  const { state } = useStore()
  const { identity } = state

  const { id } = useParams()

  const [query, setQuery] = useState('')
  const [isEditing, setIsEditing] = useState(true)
  const [note, setNote] = useState('')
  const [selectedNote, setSelectedNote] = useState(null)
  const [notes, setNotes] = useState([])

  const [loading, setLoading] = useState(false)
  const [errorSaving, setErrorSaving] = useState(false)
  const [errorLoading, setErrorLoading] = useState(false)

  const [isDeleting, setIsDeleting] = useState(null)

  const { createAlert, createGenericErrorAlert } = useAlert()

  const [currentOpenModal, setCurrentOpenModal] = useState(null)
  const [currentTask, setCurrentTask] = useState(null)
  const [currentOpenModalStaging, setCurrentOpenModalStaging] = useState(
    TASK_TYPES.PHONE_CALL
  )
  const [selectedCustomer, setSelectedCustomer] = useState(
    customerQuery.customer
  )
  const [selectedContact, setSelectedContact] = useState(null)

  const commitNote = () => {
    const requestMethod = selectedNote ? authedAxios.put : authedAxios.post

    requestMethod(`/api/customer/${id}/note`, { note })
      .then((res) => {
        const newNote = {
          ...res.data.note,
          authorName: `${identity.firstName} ${identity.lastName}`,
        }
        setNotes([newNote].concat(notes))
        setNote('')
        // setIsEditing(false)
      })
      .catch((_e) => setErrorSaving(true))
  }

  const updateNote = (note) => {
    return authedAxios
      .put(`/api/customer/${id}/note/${note.id}`, {
        note: note.note,
        statusTypeId: note.statusTypeId,
        taskId: note.taskId,
      })
      .then((res) => {
        setNotes((prev) => updateObjectWithMatchingId(prev, note, false))
        createAlert({ message: 'Note saved successfully' })
      })
      .catch(() => createGenericErrorAlert())
  }

  const deleteNote = (note) => {
    setNotes((prev) => prev.filter((p) => p.id !== note.id))
    return authedAxios.delete(`/api/customer/${id}/note/${note.id}`, { note })
  }

  const handleEnter = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return
    commitNote()
  }

  const getNotes = () => {
    if (id === 'new') return
    setLoading(true)
    authedAxios
      .get(`/api/customer/${id}/note?query=${query}`)
      .then((res) => {
        setLoading(false)
        setNotes(res.data.notes)
      })
      .catch((_e) => {
        setErrorLoading(true)
      })
  }

  useEffect(() => {
    getNotes()
  }, [query, id])

  const activeTasks = notes.filter((n) => [1, 2].includes(n.statusTypeId))
  const completedTasks = notes.filter((n) => n.statusTypeId === 3)

  const otherNotes = notes.filter((n) => ![1, 2, 3].includes(n.statusTypeId))

  const { taskStatusTypes, ..._taskStatusTypes } = useTaskStatusTypes()

  return (
    <>
      <AllTaskModals
        currentOpenModal={currentOpenModal}
        currentOpenModalStaging={currentOpenModalStaging}
        setCurrentOpenModal={setCurrentOpenModal}
        setCurrentOpenModalStaging={setCurrentOpenModalStaging}
        selectedContact={selectedContact}
        setSelectedContact={setSelectedContact}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        updateTaskInList={() => {}}
        currentTask={currentTask}
        onClose={() => {
          createAlert({ message: 'Successfully created task' })
        }}
      />
      <CustomerHeader
        customerTicketsQuery={customerTicketsQuery}
        customer={customerQuery.customer}
        customerStatusTypesData={customerStatusTypesData}
        types={types}
        Icon={Chat}
        tooltip={
          <Tooltip
            infoText={
              'In this section you can keep track of all of your historic notes and actionable tasks by date.'
            }
          />
        }
      />
      <DeleteConfirmModal
        isOpen={!!isDeleting}
        onSubmit={() => {
          deleteNote(isDeleting)
          setIsDeleting(null)
        }}
        onClose={() => setIsDeleting(null)}
        deleteString={'this note'}
      />
      <Card>
        <CardContent>
          <Box
            className='notes-header-container'
            sx={{ alignItems: 'flex-start' }}
          >
            <TextField
              label='Search'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant='outlined'
              onClick={() => setCurrentOpenModal('selector')}
              endIcon={<Add />}
            >
              Add Task
            </Button>
          </Box>

          {isEditing && (
            <Accordion sx={{ mt: 3 }} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <b>Write a note</b> <Edit sx={{ ml: 1 }} color='primary' />
                  </Box>
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex' }}>
                  <Box sx={{ flexGrow: 1, mr: 2, position: 'relative' }}>
                    <Box>
                      <TextField
                        sx={{ height: '100%', backgroundColor: 'transparent' }}
                        fullWidth
                        label={'Note'}
                        multiline
                        rows={6}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        InputProps={{
                          sx: { backgroundColor: 'white' },
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 1,
                      }}
                    >
                      <Button
                        disabled={!note}
                        onClick={commitNote}
                        color={'primary'}
                        variant={'contained'}
                      >
                        Save Note
                      </Button>
                    </Box>
                  </Box>

                  <SummaryCard
                    customerQuery={customerQuery}
                    customerContactsQuery={customerContactsQuery}
                    dropdowns={dropdowns}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {notes.length === 0 && (
            <Typography sx={{ color: grey['700'] }}>
              <i>There are no notes for this account</i>
            </Typography>
          )}

          {activeTasks.length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>
                  <b>Active Tasks</b>
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {activeTasks.map((n, i) => (
                    <Note
                      taskStatusTypes={taskStatusTypes}
                      canEdit={identity.id === n.authorIdentityId}
                      note={n}
                      key={n.id}
                      updateNote={updateNote}
                      deleteNote={() => setIsDeleting(n)}
                      index={i}
                    />
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
          {completedTasks.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>
                  <b>Completed Tasks</b>
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {completedTasks.map((n, i) => (
                    <Note
                      taskStatusTypes={taskStatusTypes}
                      canEdit={identity.id === n.authorIdentityId}
                      note={n}
                      key={n.id}
                      updateNote={updateNote}
                      deleteNote={() => setIsDeleting(n)}
                      index={i}
                    />
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {otherNotes.length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>
                  <b>Notes</b>
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {otherNotes.map((n, i) => (
                    <Note
                      canEdit={identity.id === n.authorIdentityId}
                      note={n}
                      key={n.id}
                      updateNote={updateNote}
                      deleteNote={() => setIsDeleting(n)}
                      index={i}
                    />
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </CardContent>
      </Card>
    </>
  )
}

export default Notes
